import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft as ArrowLeftIcon,
  Star as StarIcon,
  Edit as EditIcon,
  Plus as PlusIcon,
  X as XIcon,
} from 'lucide-react';
import { motion } from 'framer-motion';

// shadcn/ui components
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';

// SalonHub modules
import { Customer } from '@/utils/localStorage';
import { TreatmentRecord, TreatmentRecordInput } from '@/types/treatment';
import { getCustomers } from '@/utils/localStorage';
import { TreatmentService } from '@/services/treatmentService';
import { TreatmentManager } from '@/components/treatment-manager/TreatmentManager';
import TimelineView from '@/components/treatment-manager/TreatmentTimeline/TimelineView';
import CustomerInsights from '@/components/CustomerInsights';
import { SampleDataGenerator } from '@/utils/sampleDataGenerator';
import { CustomerEditForm } from '@/components/customers/CustomerEditForm';
import { usePageTitle } from '@/hooks/usePageTitle';
import customersService from '@/services/customersService';
import logger from '../utils/logger';

// Utilities
const getRankColor = (rank: string): string => {
  switch (rank) {
    case 'platinum':
      return 'bg-gradient-to-r from-gray-200 to-gray-300';
    case 'gold':
      return 'bg-gradient-to-r from-yellow-400 to-yellow-500';
    case 'silver':
      return 'bg-gradient-to-r from-gray-300 to-gray-400';
    case 'bronze':
      return 'bg-gradient-to-r from-amber-600 to-amber-700';
    default:
      return 'bg-gray-400';
  }
};

const CustomerDetail: React.FC = () => {
  usePageTitle('顧客詳細');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isMobile, setIsMobile] = useState(false);

  // Existing states
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [selectedTab, setSelectedTab] = useState<string>('history');
  const [loading, setLoading] = useState(true);

  // Modal states
  const [treatmentModalOpen, setTreatmentModalOpen] = useState(false);
  const [customerEditOpen, setCustomerEditOpen] = useState(false);
  const [selectedTreatment, setSelectedTreatment] = useState<TreatmentRecord | null>(null);
  const [modalMode, setModalMode] = useState<'view' | 'edit' | 'create'>('view');

  // Data refresh key
  const [refreshKey, setRefreshKey] = useState(0);

  // Snackbar state
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  // Mobile breakpoint detection
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Load customer data
  useEffect(() => {
    if (id) {
      loadCustomerData();
    }
  }, [id]);

  // Handle tab parameter from URL query
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam) {
      switch (tabParam) {
        case 'history':
          setSelectedTab('history');
          break;
        case 'insights':
          setSelectedTab('insights');
          break;
        case 'edit':
          setSelectedTab('history');
          setCustomerEditOpen(true);
          break;
        default:
          setSelectedTab('history');
          break;
      }
    }
  }, [searchParams]);

  const loadCustomerData = async () => {
    try {
      // Try API first, fallback to localStorage
      try {
        const apiCustomer = await customersService.getCustomerById(id!);
        setCustomer(apiCustomer as any);
      } catch (apiError) {
        logger.warn('API call failed, using localStorage:', apiError);
        const customers = getCustomers();
        const foundCustomer = customers.find((c) => String(c.id) === String(id));
        if (foundCustomer) {
          setCustomer(foundCustomer);
        } else {
          navigate('/customers');
        }
      }
    } catch (error) {
      logger.error('Error loading customer:', error);
      navigate('/customers');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/customers');
  };

  // Sample data insertion (dev mode only)
  const handleInsertSampleData = async () => {
    if (customer?.id && import.meta.env.DEV) {
      if (window.confirm('サンプルデータを3件挿入しますか？')) {
        try {
          await SampleDataGenerator.insertSampleData(customer.id);
          setSnackbar({
            open: true,
            message: 'サンプルデータを挿入しました',
            severity: 'success',
          });
          setRefreshKey((prev) => prev + 1);
        } catch (error) {
          setSnackbar({
            open: true,
            message: 'サンプルデータの挿入に失敗しました',
            severity: 'error',
          });
          logger.error('Failed to insert sample data:', error);
        }
      }
    }
  };

  // Treatment modal handlers
  const openTreatmentModal = (mode: 'view' | 'edit' | 'create', record?: TreatmentRecord) => {
    setModalMode(mode);
    setSelectedTreatment(record || null);
    setTreatmentModalOpen(true);
  };

  const closeTreatmentModal = () => {
    setTreatmentModalOpen(false);
    setSelectedTreatment(null);
  };

  // Treatment save handler
  const handleTreatmentSave = async (data: TreatmentRecordInput) => {
    try {
      if (modalMode === 'create') {
        await TreatmentService.saveTreatmentRecord(customer!.id, data);
        setSnackbar({
          open: true,
          message: '施術記録を作成しました',
          severity: 'success',
        });
      } else if (modalMode === 'edit' && selectedTreatment) {
        await TreatmentService.updateTreatmentRecord(
          customer!.id,
          selectedTreatment.id,
          data as Partial<TreatmentRecord>
        );
        setSnackbar({
          open: true,
          message: '施術記録を更新しました',
          severity: 'success',
        });
      }

      closeTreatmentModal();
      setRefreshKey((prev) => prev + 1);
    } catch (error) {
      logger.error('保存エラー:', error);
      setSnackbar({
        open: true,
        message: error instanceof Error ? error.message : '保存に失敗しました',
        severity: 'error',
      });
      throw error;
    }
  };

  // Customer edit handlers
  const handleEditCustomer = () => {
    setCustomerEditOpen(true);
  };

  const handleEditClose = () => {
    setCustomerEditOpen(false);
  };

  const handleCustomerUpdate = (updatedCustomer: Customer) => {
    setCustomer(updatedCustomer);
    setCustomerEditOpen(false);
    setSnackbar({
      open: true,
      message: '顧客情報を更新しました',
      severity: 'success',
    });
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  if (loading) {
    return (
      <div className="p-6">
        <p className="text-muted-foreground">読み込み中...</p>
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="p-6">
        <p className="text-muted-foreground">顧客が見つかりません</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="bg-white border-b"
      >
        <div className="px-4 py-3">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={handleBack}>
              <ArrowLeftIcon className="h-5 w-5" />
            </Button>

            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink
                    onClick={handleBack}
                    className="cursor-pointer hover:text-primary"
                  >
                    顧客管理
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbPage>{customer.name}</BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
          </div>
        </div>
      </motion.div>

      {/* Main Content */}
      <div className="p-4 sm:p-6 md:p-8">
        {/* Customer Info Card */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
        >
          <Card className="mb-6">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <Avatar className={`h-20 w-20 ${getRankColor(customer.rank || 'bronze')}`}>
                  <AvatarFallback className="text-white text-2xl font-semibold">
                    {customer.name.charAt(0)}
                  </AvatarFallback>
                </Avatar>

                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h2 className="text-2xl font-bold">{customer.name}</h2>
                    {customer.isVip && (
                      <Badge variant="default" className="gap-1">
                        <StarIcon className="h-3 w-3" />
                        VIP
                      </Badge>
                    )}
                    <span className="text-sm text-muted-foreground">{customer.customerCode}</span>
                  </div>

                  {/* Tags */}
                  {customer.tags && customer.tags.length > 0 && (
                    <div className="flex gap-2 flex-wrap mb-4">
                      {customer.tags.map((tag: string) => (
                        <Badge key={tag} variant="outline" className="text-xs">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  )}

                  {/* Basic Information Section */}
                  <div>
                    <Label className="text-sm font-semibold text-muted-foreground mb-3 block">
                      基本情報
                    </Label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs text-muted-foreground">氏名（カナ）</Label>
                        <p className="text-sm">{customer.nameKana || '-'}</p>
                      </div>
                      <div className="flex gap-6">
                        <div>
                          <Label className="text-xs text-muted-foreground">誕生日</Label>
                          <p className="text-sm">
                            {customer.birthDate
                              ? new Date(customer.birthDate).toLocaleDateString('ja-JP')
                              : '-'}
                          </p>
                        </div>
                        <div>
                          <Label className="text-xs text-muted-foreground">性別</Label>
                          <p className="text-sm">
                            {customer.gender
                              ? (
                                  {
                                    male: '男性',
                                    female: '女性',
                                    other: 'その他',
                                    unspecified: '未指定',
                                  } as Record<string, string>
                                )[customer.gender]
                              : '-'}
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-6">
                        <div>
                          <Label className="text-xs text-muted-foreground">電話番号</Label>
                          <p className="text-sm">{customer.phone}</p>
                        </div>
                        {customer.phoneSecondary && (
                          <div>
                            <Label className="text-xs text-muted-foreground">電話番号2</Label>
                            <p className="text-sm">{customer.phoneSecondary}</p>
                          </div>
                        )}
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">メールアドレス</Label>
                        <p className="text-sm">{customer.email || '-'}</p>
                      </div>
                    </div>
                  </div>

                  <Separator className="my-4" />

                  {/* Visit Information Section */}
                  <div>
                    <Label className="text-sm font-semibold text-muted-foreground mb-3 block">
                      来店情報
                    </Label>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                      <div>
                        <Label className="text-xs text-muted-foreground">初回来店日</Label>
                        <p className="text-sm">
                          {customer.firstVisitDate
                            ? new Date(customer.firstVisitDate).toLocaleDateString('ja-JP')
                            : customer.createdAt
                            ? new Date(customer.createdAt).toLocaleDateString('ja-JP')
                            : '-'}
                        </p>
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">最終来店日</Label>
                        <p className="text-sm">
                          {customer.lastVisit
                            ? new Date(customer.lastVisit).toLocaleDateString('ja-JP')
                            : '-'}
                        </p>
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">累計来店回数</Label>
                        <p className="text-sm">{customer.totalVisits || 0}回</p>
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">予約経路</Label>
                        <p className="text-sm">
                          {customer.bookingSource
                            ? (
                                {
                                  hotpepper: 'ホットペッパービューティー',
                                  rakuten: '楽天ビューティー',
                                  google: 'Google予約',
                                  instagram: 'Instagram',
                                  referral: '紹介',
                                  'walk-in': '飛び込み',
                                  other: 'その他',
                                } as Record<string, string>
                              )[customer.bookingSource] || customer.bookingSource
                            : '-'}
                        </p>
                      </div>
                    </div>
                  </div>

                  <Separator className="my-4" />

                  {/* Other Information Section */}
                  <div>
                    <Label className="text-sm font-semibold text-muted-foreground mb-3 block">
                      その他情報
                    </Label>
                    <div className="mb-3">
                      <Label className="text-xs text-muted-foreground">住所</Label>
                      <p className="text-sm">{customer.address || '-'}</p>
                    </div>
                  </div>

                  {/* Customer Memo */}
                  {customer.memo && (
                    <div className="mt-4 p-3 bg-gray-50 rounded-md">
                      <Label className="text-xs text-muted-foreground">顧客メモ</Label>
                      <p className="text-sm mt-1 whitespace-pre-wrap">{customer.memo}</p>
                    </div>
                  )}
                </div>

                <Button variant="ghost" size="icon" onClick={handleEditCustomer}>
                  <EditIcon className="h-5 w-5" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Tabs Section */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.2 }}
        >
          <Card>
            <Tabs value={selectedTab} onValueChange={setSelectedTab}>
              <CardHeader className="pb-3">
                <TabsList className="w-full sm:w-auto">
                  <TabsTrigger value="history" className="flex-1 sm:flex-none">
                    カルテ
                  </TabsTrigger>
                  <TabsTrigger value="insights" className="flex-1 sm:flex-none">
                    顧客インサイト
                  </TabsTrigger>
                </TabsList>
              </CardHeader>

              <CardContent className="min-h-[600px]">
                <TabsContent value="history" className="mt-0">
                  <div>
                    {/* Action Header */}
                    <div className="mb-6 flex justify-between items-center flex-wrap gap-3">
                      <h3 className="text-xl font-semibold">施術履歴</h3>
                      <div className="flex gap-2">
                        {import.meta.env.DEV && (
                          <Button variant="outline" onClick={handleInsertSampleData}>
                            サンプルデータ挿入（開発用）
                          </Button>
                        )}
                        <Button onClick={() => openTreatmentModal('create')}>
                          <PlusIcon className="mr-2 h-4 w-4" />
                          新規施術記録
                        </Button>
                      </div>
                    </div>

                    {/* Timeline View */}
                    <TimelineView
                      key={refreshKey}
                      customerId={customer.id}
                      onSelectRecord={(record) => openTreatmentModal('view', record)}
                    />
                  </div>
                </TabsContent>

                <TabsContent value="insights" className="mt-0">
                  <CustomerInsights customerId={customer!.id} />
                </TabsContent>
              </CardContent>
            </Tabs>
          </Card>
        </motion.div>
      </div>

      {/* Treatment Modal */}
      <Dialog
        open={treatmentModalOpen}
        onOpenChange={(open) => !open && closeTreatmentModal()}
      >
        <DialogContent
          className={`max-w-4xl ${isMobile ? 'h-full max-h-screen' : 'max-h-[90vh]'}`}
        >
          <DialogHeader>
            <div className="flex justify-between items-center">
              <DialogTitle>
                {modalMode === 'create'
                  ? '新規施術記録'
                  : modalMode === 'edit'
                  ? '施術記録編集'
                  : '施術記録詳細'}
              </DialogTitle>
              <Button variant="ghost" size="icon" onClick={closeTreatmentModal}>
                <XIcon className="h-4 w-4" />
              </Button>
            </div>
          </DialogHeader>
          <div className="overflow-y-auto">
            <TreatmentManager
              customerId={customer.id}
              mode={modalMode}
              initialData={
                selectedTreatment ||
                ({
                  customerName: customer.name,
                  date: new Date(),
                  staffName: customer.preferredStaff || '',
                  physicalCondition: '良好',
                  symptoms: [],
                  treatmentMenu: '',
                  treatmentDuration: 60,
                  treatmentAmount: 0,
                  productAmount: 0,
                  paymentMethod: 'cash',
                  isFirstVisit: false,
                  notes: {
                    before: '',
                    during: '',
                    after: '',
                    conversation: '',
                  },
                  techniques: [],
                  focusAreas: [],
                  improvements: [],
                  feedback: '',
                  requests: '',
                  concerns: '',
                  lifestyle: {},
                  staffId: 1,
                  status: 'completed',
                } as Partial<TreatmentRecord>)
              }
              onSave={handleTreatmentSave}
              onCancel={closeTreatmentModal}
              onModeChange={setModalMode}
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* Customer Edit Modal */}
      {customerEditOpen && customer && (
        <CustomerEditForm
          customer={customer}
          open={customerEditOpen}
          onClose={handleEditClose}
          onSave={handleCustomerUpdate}
        />
      )}

      {/* Snackbar */}
      {snackbar.open && (
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 50 }}
          className="fixed bottom-4 left-1/2 transform -translate-x-1/2 z-50"
        >
          <Alert variant={snackbar.severity === 'error' ? 'destructive' : 'default'}>
            <AlertDescription className="flex items-center gap-3">
              <span>{snackbar.message}</span>
              <Button size="sm" variant="ghost" onClick={handleSnackbarClose}>
                閉じる
              </Button>
            </AlertDescription>
          </Alert>
        </motion.div>
      )}
    </div>
  );
};

export default CustomerDetail;
